import { z } from 'zod';
import { photoMetaWireFields } from './photo';

/**
 * Wire schemas for note API responses
 * These match the backend response format (snake_case)
 *
 * IMPORTANT: Backend stores:
 * - Timestamps as ISO string DATETIME, not Unix numbers
 * - Optional fields can be null, not just undefined
 */

/**
 * Note photo wire schema (matches backend NotePhotoReadSchema)
 */
export const notePhotoWireSchema = z.object({
  id: z.string(),
  note_id: z.string(),
  r2_key: z.string(),
  image_url: z.string().url(),
  created_at: z.string(), // D1 DATETIME (ISO format)
  ...photoMetaWireFields,
});

export type NotePhotoWire = z.infer<typeof notePhotoWireSchema>;

/**
 * Note wire schema (matches backend NoteReadSchema)
 */
export const noteWireSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  business_id: z.string(),
  menu_item_id: z.string(),
  content: z.string(),
  created_at: z.string(), // D1 DATETIME (ISO format)
  updated_at: z.string(), // D1 DATETIME (ISO format)
  // Optional populated fields from JOINs
  business_name: z.string().optional(),
  menu_item_name: z.string().optional(),
  photo: notePhotoWireSchema.nullable().optional(),
});

export type NoteWire = z.infer<typeof noteWireSchema>;

/**
 * Note creation data
 */
export const noteCreateSchema = z.object({
  business_id: z.string().min(1),
  menu_item_id: z.string().min(1),
  content: z.string().min(1, 'Content is required').max(2000, 'Content must be 2000 characters or less'),
});

export type NoteCreate = z.infer<typeof noteCreateSchema>;

/**
 * Note update data
 */
export const noteUpdateSchema = z.object({
  content: z.string().min(1, 'Content is required').max(2000, 'Content must be 2000 characters or less'),
});

export type NoteUpdate = z.infer<typeof noteUpdateSchema>;

/**
 * Paginated response wrapper schema
 * Matches createPaginatedResponseSchema from Gateway
 */
export const paginatedNoteResponseSchema = z.object({
  items: z.array(noteWireSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1).max(100),
  total_pages: z.number().int().min(0),
});

export type PaginatedNoteResponseWire = z.infer<typeof paginatedNoteResponseSchema>;
